import { useState } from 'react'

import { Badge } from '@mantine/core'

import DnTable from 'src/components/Core/DnTable'
import DetailSection from 'src/components/DataDisplay/DetailSection/DetailSection'
import { formatDateWithTime } from 'src/lib/formatters'

import type { ContactHistoryEntry } from '../carrier.types'
import { contactLabel } from '../carrier.utils'
import { describeChange } from '../contactHistory.utils'

import CarrierContactHistoryDrawer from './CarrierContactHistoryDrawer'

export interface CarrierContactHistoryProps {
  entries: ContactHistoryEntry[]
  /** Size of the whole history, not just this page. */
  totalCount: number
  pageSize: number
  /** 1-based, so a reset made by the caller reaches the paginator. */
  currentPage?: number
  onLoadPage: (pageInfo: { offset: number; limit: number }) => void
  /** Set while the history is narrowed to one contact. */
  filteredContactLabel?: string | null
  onClearFilter?: () => void
  actions?: React.ReactNode
}

const SECTION_DESCRIPTION =
  'Every recorded change to this carrier’s contacts, newest first — who changed what, and when. It spans the carrier rather than one contact, because a contact merged into a sibling or archived is no longer in the table above, and those are the entries most worth reading. An entry with no name against it was written by the harvest path rather than by a person: most contacts arrive from a driver’s application, not from somebody typing.'

const CHANGE_TYPE_LABEL: Record<string, string> = {
  CREATE: 'Added',
  UPDATE: 'Changed',
  DELETE: 'Removed',
}

const CHANGE_TYPE_COLOR: Record<string, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
}

const blank = <span className="italic text-doubleNickel-gray-400">nothing</span>

/**
 * The audit trail behind a carrier's contacts.
 *
 * Takes its rows rather than fetching them, so it serves a query result, a
 * story and a test alike.
 */
const CarrierContactHistory = ({
  entries,
  totalCount,
  pageSize,
  currentPage = 1,
  onLoadPage,
  filteredContactLabel,
  onClearFilter,
  actions,
}: CarrierContactHistoryProps) => {
  // Held as the entry rather than an id: the row the reader clicked is the
  // whole source, so the drawer never waits on a fetch or goes stale against
  // a page that has since moved.
  const [opened, setOpened] = useState<ContactHistoryEntry | null>(null)

  const columns = [
    {
      key: 'createdAt',
      label: 'When',
      width: 14,
      render: (entry: ContactHistoryEntry) => (
        <span className="whitespace-nowrap text-xs text-doubleNickel-gray-600">
          {formatDateWithTime(entry.createdAt)}
        </span>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      width: 18,
      render: (entry: ContactHistoryEntry) => (
        <span className="text-sm text-doubleNickel-gray-900">
          {contactLabel(entry.contact)}
        </span>
      ),
    },
    {
      key: 'changeType',
      label: '',
      width: 8,
      render: (entry: ContactHistoryEntry) => (
        <Badge
          size="sm"
          variant="light"
          color={CHANGE_TYPE_COLOR[entry.changeType] ?? 'gray'}
        >
          {CHANGE_TYPE_LABEL[entry.changeType] ?? entry.changeType}
        </Badge>
      ),
    },
    {
      key: 'changes',
      label: 'What changed',
      width: 30,
      render: (entry: ContactHistoryEntry) =>
        entry.changes.length === 0 ? (
          // The audit trail records an update whether or not it moved
          // anything, so a row with nothing in it is a real outcome — a save
          // that wrote the values already on file — and not a rendering gap.
          <span className="text-xs italic text-doubleNickel-gray-400">
            Nothing changed
          </span>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {entry.changes.map((change) => {
              // The API reports what moved, as stored; the words are chosen
              // here, alongside the rest of this screen's.
              const { label, previousValue, newValue } = describeChange(change)

              return (
                <li key={change.field} className="text-xs">
                  <span className="text-doubleNickel-gray-500">{label}: </span>
                  <span className="text-doubleNickel-gray-400 line-through">
                    {previousValue || blank}
                  </span>
                  <span className="text-doubleNickel-gray-400"> → </span>
                  <span className="text-doubleNickel-gray-900">
                    {newValue || blank}
                  </span>
                </li>
              )
            })}
          </ul>
        ),
    },
    {
      key: 'note',
      label: 'Note',
      width: 16,
      // Clamped rather than wrapped: a long reason would otherwise set the
      // height of a row whose other cells are one line. The drawer has it in
      // full, which is what the row click is for.
      render: (entry: ContactHistoryEntry) =>
        entry.note ? (
          <span className="line-clamp-2 text-xs italic text-doubleNickel-gray-600">
            {entry.note}
          </span>
        ) : (
          <span className="text-xs text-doubleNickel-gray-300">—</span>
        ),
    },
    {
      key: 'actorName',
      label: 'Who',
      width: 14,
      render: (entry: ContactHistoryEntry) =>
        entry.actorName ? (
          <span className="text-xs text-doubleNickel-gray-700">
            {entry.actorName}
          </span>
        ) : (
          <span className="text-xs italic text-doubleNickel-gray-400">
            Automated
          </span>
        ),
    },
  ]

  return (
    <DetailSection
      title="Contact History"
      description={SECTION_DESCRIPTION}
      columns={1}
      actions={actions}
    >
      {filteredContactLabel && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-25 px-3 py-2">
          <span className="text-sm text-doubleNickel-gray-700">
            Showing {filteredContactLabel} only
          </span>
          {onClearFilter && (
            <button
              type="button"
              className="text-xs text-doubleNickel-brand-700 underline"
              onClick={onClearFilter}
            >
              Show all contacts
            </button>
          )}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="py-2 text-sm text-doubleNickel-gray-500">
          {filteredContactLabel
            ? 'Nothing recorded against this contact yet.'
            : 'Nothing recorded against this carrier’s contacts yet.'}
        </div>
      ) : (
        <DnTable
          columns={columns}
          data={entries}
          totalCount={totalCount}
          defaultResultsPerPage={pageSize}
          paginationProps={{ initialPage: currentPage }}
          onLoadPage={onLoadPage}
          onRowClick={(_index, entry) => setOpened(entry)}
        />
      )}

      <CarrierContactHistoryDrawer
        entry={opened}
        onClose={() => setOpened(null)}
      />
    </DetailSection>
  )
}

export default CarrierContactHistory
